// Shared permission-map helpers for the role and user permission editors.

/** Actions hold booleans; a page's limit keys (RegistryPage.limits, e.g. discountLimitPercent) hold numbers. */
export type PermissionsMap = Record<string, Record<string, boolean | number> | boolean>;
export type RegistryLimit = { key: string; label: string; unit: '%' | 'GBP' };
export type RegistryPage = { key: string; label: string; group: string; actions: string[]; region: string | null; limits?: RegistryLimit[] };
export type DevelopmentTab = { key: string; label: string; pages: string[]; subTabs?: Array<{ label: string; pages: string[] }> };
export type Registry = { groups: string[]; pages: RegistryPage[]; developmentTabs?: DevelopmentTab[] };
export type PermissionTemplate = { id: string; label: string; build: () => PermissionsMap };

/** Friendly names for the registry's action keys; anything not listed is shown as a sentence-cased key. */
const ACTION_LABELS: Record<string, string> = { read: 'View', create: 'Create', update: 'Edit', delete: 'Delete', export: 'Export', approve: 'Approve' };
export const actionLabel = (a: string) => ACTION_LABELS[a] ?? a.replace(/([A-Z])/g, ' $1').replace(/^\w/, (c) => c.toUpperCase());

/** View last, Edit just before it, everything else first — so the columns line up down a group. */
const RANK: Record<string, number> = { create: 1, delete: 2, export: 3, update: 98, read: 99 };
export const ordered = (actions: string[]) => [...actions].sort((a, b) => (RANK[a] ?? 50) - (RANK[b] ?? 50));

/** Data scope types a role (or user) can have, with what each means. */
export const ROLE_SCOPES: Array<[string, string]> = [
  ['ALL', 'Everything they are permitted to see'],
  ['REGION', 'Only records in their region'],
  ['ENTITY', 'Only records of selected companies'],
  ['PROPERTY', 'Only records at selected properties'],
  ['OWN', 'Only records assigned to or owned by them'],
];

export const has = (map: PermissionsMap | undefined, page: string, action: string) => { const p = map?.[page]; return p === true || (typeof p === 'object' && !!p?.[action]); };

/** A page's numeric limit as stored (undefined when not set). */
export const limitOf = (map: PermissionsMap | undefined, page: string, key: string): number | undefined => {
  const p = map?.[page]; const v = typeof p === 'object' ? p?.[key] : undefined;
  return typeof v === 'number' ? v : undefined;
};

/** One page's grants as an explicit per-action object (`true` for a page means every action). Its limits ride along. */
export const expandPage = (page: RegistryPage, raw: PermissionsMap[string] | undefined): Record<string, boolean | number> => ({
  ...Object.fromEntries(page.actions.map((a) => [a, raw === true || (typeof raw === 'object' && !!raw?.[a])])),
  ...Object.fromEntries((page.limits ?? []).flatMap((l) => { const v = typeof raw === 'object' ? raw?.[l.key] : undefined; return typeof v === 'number' ? [[l.key, v]] : []; })),
});

/** Every page in the registry, explicitly — never a sparse map, which the server could read as "unconfigured". */
export const explicitMap = (registry: Registry, source: PermissionsMap | 'all' | 'read' | 'none'): PermissionsMap =>
  Object.fromEntries(registry.pages.map((p) => [p.key, typeof source === 'object'
    ? expandPage(p, source[p.key])
    : Object.fromEntries(p.actions.map((a) => [a, source === 'all' || (source === 'read' && a === 'read')]))]));


/**
 * Only the actions in `overrides` that `base` (the role) does not already grant, as `{ page: { action: true } }`.
 * User overrides are add-only on the server, so anything else in the map is dead weight.
 */
export const extrasOnly = (registry: Registry, overrides: PermissionsMap | undefined, base: PermissionsMap | undefined, baseAll = false): PermissionsMap => {
  if (baseAll) return {};
  const out: PermissionsMap = {};
  for (const p of registry.pages) {
    const extra = p.actions.filter((a) => has(overrides, p.key, a) && !has(base, p.key, a));
    // A limit set for this person that differs from the role's replaces it (the server layers it like an action).
    const limits = (p.limits ?? []).flatMap((l) => { const v = limitOf(overrides, p.key, l.key); return v !== undefined && v !== limitOf(base, p.key, l.key) ? [[l.key, v] as const] : []; });
    if (extra.length || limits.length) out[p.key] = { ...Object.fromEntries(extra.map((a) => [a, true])), ...Object.fromEntries(limits) };
  }
  return out;
};

/** Number of granted actions in a map, counted against the registry. */
export const countActions = (map: PermissionsMap | undefined, registry?: Registry) => registry?.pages.reduce((n, p) => n + p.actions.filter((a) => has(map, p.key, a)).length, 0) ?? 0;
